import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import { ArrowRightLeft, Eye, Pencil } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableTableHead } from "@/components/ui/sortable-table-head"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { StageChip } from "@/components/StageChip"
import type { Asset, InventoryFilters } from "@/types/inventory"
import type { AssetType } from "@/lib/assetTypes"
import { formatVolume } from "@/lib/format"

interface InventoryTableProps {
  assets: Asset[]
  assetType: AssetType
  filters: InventoryFilters
  onSort: (column: string) => void
  onEdit: (asset: Asset) => void
  selectedIds?: Set<string>
  onSelectionChange?: (ids: Set<string>) => void
  onMoveStage?: (asset: Asset) => void
}

function getContrastColor(hexColor: string): string {
  const hex = hexColor.replace("#", "")
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? "#000000" : "#ffffff"
}

function formatDate(dateString: string | null): string {
  if (!dateString) return "-"
  const date = new Date(dateString)
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })
}



export function InventoryTable({
  assets, assetType, filters, onSort, onEdit, selectedIds, onSelectionChange, onMoveStage,
}: InventoryTableProps) {
  const { t } = useTranslation()
  const showSelection = selectedIds !== undefined && onSelectionChange !== undefined
  const isKeg = assetType === "Keg"

  const allPageSelected = showSelection && assets.length > 0 && assets.every((a) => selectedIds.has(a.id))
  const somePageSelected = showSelection && assets.some((a) => selectedIds.has(a.id))

  function handleSelectAll(checked: boolean) {
    if (!onSelectionChange || !selectedIds) return
    const newSelection = new Set(selectedIds)
    if (checked) { assets.forEach((a) => newSelection.add(a.id)) }
    else { assets.forEach((a) => newSelection.delete(a.id)) }
    onSelectionChange(newSelection)
  }

  function handleSelectOne(assetId: string, checked: boolean) {
    if (!onSelectionChange || !selectedIds) return
    const newSelection = new Set(selectedIds)
    if (checked) { newSelection.add(assetId) } else { newSelection.delete(assetId) }
    onSelectionChange(newSelection)
  }

  return (
    <div className="overflow-x-auto">
      <Table className="table-fixed">
        <TableHeader className="bg-secondary/40 [&_th]:font-mono [&_th]:uppercase [&_th]:text-[10.5px] [&_th]:tracking-[0.1em] [&_th]:text-muted-foreground [&_th]:font-semibold">
          <TableRow>
            {showSelection && (
              <TableHead className="w-12">
                <Checkbox checked={allPageSelected} onCheckedChange={handleSelectAll} aria-label={t("inventory.selectAll")}
                  {...(somePageSelected && !allPageSelected ? { "data-state": "indeterminate" } : {})} />
              </TableHead>
            )}
            <SortableTableHead label={t("inventory.table.lastEvent")} sortKey="last_event_at" currentSortKey={filters.sort} direction={filters.direction} onSort={onSort} className="w-28" />
            <SortableTableHead label={t("inventory.table.code")} sortKey="code" currentSortKey={filters.sort} direction={filters.direction} onSort={onSort} className="w-24" />
            {isKeg && (
              <SortableTableHead label={t("inventory.table.product")} sortKey="product" currentSortKey={filters.sort} direction={filters.direction} onSort={onSort} className="hidden md:table-cell w-32" />
            )}
            {isKeg && (
              <SortableTableHead label={t("inventory.table.container")} sortKey="container" currentSortKey={filters.sort} direction={filters.direction} onSort={onSort} className="hidden lg:table-cell w-28" />
            )}
            {isKeg && (
              <SortableTableHead label={t("inventory.table.volume")} sortKey="volume" currentSortKey={filters.sort} direction={filters.direction} onSort={onSort} className="hidden lg:table-cell w-20" />
            )}
            {!isKeg && <TableHead className="hidden md:table-cell w-28">{t("inventory.table.brand")}</TableHead>}
            {!isKeg && <TableHead className="hidden lg:table-cell w-28">{t("inventory.table.model")}</TableHead>}
            <SortableTableHead label={t("inventory.table.stage")} sortKey="stage" currentSortKey={filters.sort} direction={filters.direction} onSort={onSort} className="w-28" />
            <TableHead className="hidden lg:table-cell w-36">{t("inventory.table.customer")}</TableHead>
            <SortableTableHead label={t("inventory.table.location")} sortKey="location" currentSortKey={filters.sort} direction={filters.direction} onSort={onSort} className="hidden xl:table-cell w-48" />
            <TableHead className="hidden sm:table-cell w-36">{t("inventory.table.tags")}</TableHead>
            <TableHead className="text-right w-32">{t("common.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {assets.map((asset) => {
            const isSelected = showSelection && selectedIds.has(asset.id)
            return (
              <TableRow
                key={asset.id}
                className={`border-t border-[var(--border-soft)] hover:bg-secondary/50 transition-colors ${isSelected ? "!bg-[var(--accent-soft)]/60" : ""}`}
              >
                {showSelection && (
                  <TableCell>
                    <Checkbox checked={isSelected} onCheckedChange={(checked) => handleSelectOne(asset.id, checked as boolean)} aria-label={t("inventory.selectAsset", { code: asset.code })} />
                  </TableCell>
                )}
                <TableCell className="truncate font-mono text-xs text-muted-foreground">{formatDate(asset.last_event_at)}</TableCell>
                <TableCell className="font-mono font-semibold truncate tracking-wide">{asset.code}</TableCell>
                {isKeg && <TableCell className="hidden md:table-cell truncate">{asset.product?.name || "-"}</TableCell>}
                {isKeg && <TableCell className="hidden lg:table-cell truncate">{asset.container?.name || "-"}</TableCell>}
                {isKeg && <TableCell className="hidden lg:table-cell truncate tabular-nums">{formatVolume(asset.volume) ?? "-"}</TableCell>}
                {!isKeg && <TableCell className="hidden md:table-cell truncate">{asset.brand || "-"}</TableCell>}
                {!isKeg && <TableCell className="hidden lg:table-cell truncate">{asset.model || "-"}</TableCell>}
                <TableCell>
                  <StageChip stage={asset.stage} />
                </TableCell>
                <TableCell className="hidden lg:table-cell truncate" title={asset.customer?.name || ""}>
                  {asset.customer?.name || "-"}
                </TableCell>
                <TableCell className="hidden xl:table-cell truncate" title={asset.location?.name || ""}>
                  {asset.location ? (
                    <div className="flex flex-col leading-tight">
                      <span className="font-medium">{asset.location.name}</span>
                      {(asset.location.city || asset.location.state) && (
                        <span className="text-[11px] font-mono tracking-wide text-muted-foreground">
                          {asset.location.city && asset.location.state ? `${asset.location.city} · ${asset.location.state}` : asset.location.city || asset.location.state}
                        </span>
                      )}
                    </div>
                  ) : "—"}
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  {asset.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {asset.tags.map((tag) => (
                        <span key={tag.id} className="inline-flex px-2 py-0.5 rounded text-xs whitespace-nowrap" style={{ backgroundColor: tag.color, color: getContrastColor(tag.color) }}>
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  ) : "-"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {onMoveStage && (
                      <Button variant="ghost" size="icon" onClick={() => onMoveStage(asset)} title={t("inventory.moveStage")}>
                        <ArrowRightLeft className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" asChild title={t("common.view")}>
                      <Link href={`/inventory/${asset.id}`}><Eye className="h-4 w-4" /></Link>
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => onEdit(asset)} title={t("common.edit")}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
